import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { UploadSimple, X, ImageSquare } from "@phosphor-icons/react";

const SECTION_LABELS = {
  executive_summary: "Executive Summary Image",
  ceo_letter: "CEO Photo",
  company_overview: "Company Images",
  esg_financial_report: "ESG Financial Report's Picture",
  environmental_overview: "Environmental Images",
  social_overview: "Social Impact Images",
  training_development: "Training & Development Images",
  community_engagement: "Community Engagement Images",
  supply_chain: "Supply Chain Images",
  section_environmental: "Environmental Section Cover Image",
  section_social: "Social Section Cover Image",
  section_governance: "Governance Section Cover Image",
};

export default function ImageUploader({ reportId, section, maxImages = 4, accentColor = "#01AF9B" }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState([]);

  const loadImages = useCallback(async () => {
    if (!reportId) return;
    try {
      const data = await api.getSectionImages(reportId, section);
      setImages(data);
    } catch { /* ignore */ }
  }, [reportId, section]);

  useEffect(() => { loadImages(); }, [loadImages]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = maxImages - images.length;
    const toUpload = files.slice(0, remaining);

    setUploading(true);
    for (const file of toUpload) {
      try {
        await api.uploadSectionImage(reportId, section, file);
      } catch (err) {
        alert(err.response?.data?.detail || "Upload failed");
        break;
      }
    }
    await loadImages();
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (index) => {
    try {
      await api.deleteSectionImage(reportId, section, index);
      await loadImages();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const label = SECTION_LABELS[section] || "Images";
  const canUpload = images.length < maxImages;

  return (
    <div className="mt-4 pt-4 border-t border-slate-800/40">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ImageSquare size={16} style={{ color: accentColor }} />
          <span className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">
            {label} ({images.length}/{maxImages})
          </span>
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {images.map((img, i) => (
            <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-700 hover:border-slate-500 transition-all" data-testid={`section-img-${section}-${i}`}>
              <div className="w-full h-full bg-[#0B0C10] flex items-center justify-center">
                <ImageSquare size={24} className="text-slate-600" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 text-[8px] text-slate-300 truncate">
                {img.filename}
              </div>
              <button onClick={() => handleDelete(i)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-img-${section}-${i}`}>
                <X size={10} weight="bold" className="text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {canUpload && reportId && (
        <label className="inline-flex items-center gap-2 bg-[#0B0C10] border border-dashed border-slate-600 rounded-lg px-4 py-2.5 text-xs text-slate-400 cursor-pointer hover:border-slate-400 hover:text-slate-300 transition-all"
          data-testid={`upload-btn-${section}`}>
          {uploading ? (
            <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <UploadSimple size={14} style={{ color: accentColor }} />
          )}
          <span>{uploading ? "Uploading..." : `Add ${maxImages === 1 ? "Image" : "Images"}`}</span>
          <input type="file" accept="image/*" multiple={maxImages > 1} className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      )}

      {!reportId && (
        <p className="text-slate-600 text-[10px]">Save the report first to enable image uploads</p>
      )}
    </div>
  );
}
